import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AppConfig, FeatureInfo, FieldInfo } from '@tellvo/setup-protocol';
import schema from '../../schema/account.schema.json' with { type: 'json' };
import { Button, Choice, Chip, Seg, Switch, ToggleChips } from './controls.js';
import { initials, number } from './format.js';
import { FormModel, transportPatch } from './form.js';
import { list, t, type Key } from './i18n/index.js';
import { IconAlert, IconChevronDown, IconKey, IconPlus, IconShield, IconSliders, IconX } from './icons.js';
import { pushNote, pushRow } from './push.js';
import type { CheckResult, SetupStore } from './store.js';
import type { AccountMark } from './sync.js';

type Entry = AppConfig['accounts'][number];

const CODEC_LABELS: Record<string, string> = { opus: 'Opus', g722: 'G.722', pcmu: 'PCMU', pcma: 'PCMA' };
/** Security and audio, between sign-in and the rest of the connection. */
const SECURITY = ['srtp', 'codecs'];
/** Fields that take the whole row of the two-column grid. */
const WIDE = new Set(['displayName', 'password', 'srtp', 'codecs', 'trustedCertificateSha256']);

/**
 * The open account, in the console's sheet. It lives on the page: typing changes the page's
 * copy and mirrors it live on the phone's screen while the phone is there; Save hands the whole
 * account over as a change the page keeps until the phone confirms it.
 */
export function Editor({ store, id, entry, isNew, online, feature, mark, check, checking, onDone }: {
  store: SetupStore;
  id: string;
  entry: Entry | undefined;
  isNew: boolean;
  online: boolean;
  /** "Calls when the app is closed" for the whole phone: available in the plan, on or off. */
  feature: FeatureInfo;
  mark: AccountMark | undefined;
  check: CheckResult | null;
  checking: boolean;
  onDone: () => void;
}) {
  const form = useMemo(() => new FormModel(schema), []);
  // What the account was when opened: "changed" is measured against it, not against later
  // snapshots of the phone (which arrive on their own and would otherwise look like edits).
  const [initial] = useState<Record<string, unknown>>(() => ({ ...(entry?.account ?? {}), id }));
  const [account, setAccount] = useState<Record<string, unknown>>(initial);
  const [raw, setRaw] = useState<Record<string, string>>(() => rawOf(form.fields, initial));
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const saved = useRef(false);
  const previewTimers = useRef(new Map<string, number>());
  const first = useRef<HTMLInputElement>(null);

  // Live preview on the phone's screen; an account the phone does not have yet opens as new there.
  useEffect(() => {
    if (store.phoneHas(id)) store.send({ kind: 'edit', target: id });
    else {
      store.send({ kind: 'edit', target: 'new' });
      const { id: _id, ...fields } = initial;
      if (Object.keys(fields).length) store.send({ kind: 'patch', account: fields });
    }
    // The first field takes the focus, the cursor at its end.
    const input = first.current;
    if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    return () => { if (!saved.current) store.send({ kind: 'discard' }); };
    // Opening is once per editor; the key remounts it for another account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = isNew || password !== undefined || JSON.stringify(account) !== JSON.stringify(initial);

  form.applyState(account);
  const hadPassword = entry?.hasPassword ?? false;
  const missing = form.missing(password !== undefined || hadPassword);
  const missingText = missing.length ? t('foot.missing', { fields: list(missing.map((k) => t(`field.${k}` as Key))) }) : '';

  const preview = (patch: Record<string, unknown>) => {
    const key = Object.keys(patch)[0] ?? '';
    window.clearTimeout(previewTimers.current.get(key));
    previewTimers.current.set(key, window.setTimeout(() => store.send({ kind: 'patch', account: patch }), 250));
  };

  const apply = (patch: Record<string, unknown>) => {
    setAccount((current) => {
      const next = { ...current };
      for (const [k, v] of Object.entries(patch)) { if (v === null) delete next[k]; else next[k] = v; }
      return next;
    });
    preview(patch);
  };

  const onText = (field: FieldInfo, text: string) => {
    setRaw((r) => ({ ...r, [field.key]: text }));
    if (field.sensitive) {
      setPassword(text === '' ? undefined : text);
      preview({ password: text });
      return;
    }
    form.applyState(account);
    const patch = form.patchFor(field, text);
    const invalid = form.isInvalid(field, text);
    setErrors((e) => {
      const next = { ...e };
      if (invalid) next[field.key] = field.type === 'integer'
        ? t('field.invalidNumber', { min: field.minimum ?? 0, max: field.maximum ?? 65535 })
        : t(`field.${field.key}.invalid` as Key);
      else delete next[field.key];
      return next;
    });
    if (patch) apply(patch);
  };

  const save = () => {
    if (missing.length || Object.keys(errors).length) return;
    saved.current = true;
    store.save({ ...account, id }, password, isNew);
    onDone();
  };

  const footHint = missingText || (!online ? t('foot.away') : !isNew && password !== undefined ? t('foot.password') : '');
  const name = String(account['displayName'] ?? '') || String(account['domain'] ?? '');
  const title = isNew ? t('editor.new') : name;
  const result = check && check.target === id ? check : null;
  const registration = entry?.registration;
  const push = isNew ? null : pushNote(entry?.push, String(account['transport'] ?? 'udp'), feature.perAccount === true);
  const row = pushRow(entry?.pushSwitch, feature);
  const active = entry?.account['enabled'] !== false;
  const connection = form.advanced.filter((f) => !SECURITY.includes(f.key));
  const security = form.advanced.filter((f) => SECURITY.includes(f.key));
  const failed = registration?.state === 'failed';
  const state: [Parameters<typeof Chip>[0]['tone'], Key] | null = isNew ? null
    : entry?.account['enabled'] === false ? ['neutral', 'state.off']
      : failed ? ['bad', 'state.error'] : registration?.state === 'registered' ? ['success', 'state.working'] : null;

  return (
    <article className="editor">
      <header className="editor__head">
        <span className="avatar" aria-hidden="true">{isNew ? <IconPlus /> : initials(name)}</span>
        <h2 id="editor-title"><span>{title}</span>{state ? <Chip tone={state[0]}>{t(state[1])}</Chip> : null}</h2>
        <span className="field__hint editor__sub">{isNew ? t('editor.new.sub') : `${number(String(account['username'] ?? ''))} · ${String(account['domain'] ?? '')}`}</span>
        <Button kind="icon" label={t('editor.close')} onClick={onDone}><IconX /></Button>
      </header>

      <div className="editor__body">
        {mark?.kind === 'failed' ? <div className="note note--bad note--icon"><IconAlert /><span>{mark.rejected ? t('notice.rejected') : t('editor.failed', { reason: mark.message ?? '' })}</span></div> : null}
        {!mark && failed && registration?.reason && !result
          ? <div className="note note--bad note--icon"><IconAlert /><span>{t('editor.registrationFailed', { reason: registration.reason })}</span></div> : null}
        {!isNew && entry ? (
          // The line's own switches act at once, like the card's, not through Save.
          <div className="lineset">
            <div className="setrow">
              <div className="setrow__what"><b>{t('accounts.active')}</b></div>
              <div className="setrow__end"><Switch on={active} label={t('accounts.toggle', { name })}
                onChange={(on) => store.send({ kind: 'enable', accountId: id, enabled: on })} /></div>
            </div>
            {row.kind !== 'none' ? (
              <div className="setrow">
                <div className="setrow__what">
                  <b>{t('features.pushIncoming')}</b>
                  {row.kind === 'paid' ? <span className="field__hint">{t('features.pushIncoming.unavailable')}</span>
                    : row.reason ? <span className={`field__hint${row.reason.warn ? ' field__hint--warn' : ''}`}>{t(row.reason.key, row.reason.params)}</span> : null}
                </div>
                <div className="setrow__end">
                  {row.kind === 'switch' && row.action === 'enable'
                    ? <Button kind="sm" onClick={() => store.send({ kind: 'feature', key: 'pushIncoming', enabled: true })}>{t('push.enable')}</Button> : null}
                  {row.kind === 'paid' ? <span className="chip chip--plain">{t('features.paid')}</span>
                    : <Switch on={row.on} disabled={row.disabled} label={t('accounts.pushToggle', { name })}
                      onChange={(on) => store.send({ kind: 'accountPush', accountId: id, enabled: on })} />}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {push ? (
          <div className={`note note--${push.tone}${push.action ? ' note--act' : ''}`}>
            <span>{t(push.key, push.params)}{push.code ? <><br /><small>{push.code}</small></> : null}</span>
            {push.action === 'useUdp' ? <Button kind="sm" onClick={() => apply({ transport: 'udp' })}>{t('push.useUdp')}</Button> : null}
            {push.action === 'enable' ? <Button kind="sm" onClick={() => store.send({ kind: 'feature', key: 'pushIncoming', enabled: true })}>{t('push.enable')}</Button> : null}
          </div>
        ) : null}

        {result ? (
          <div className="card test" aria-live="polite">
            <div className={`note note--icon ${result.registration === 'registered' ? 'note--ok' : 'note--bad'}`}>
              {result.registration === 'registered' ? null : <IconAlert />}
              <span>{result.registration === 'registered' ? t('check.registered') : t('check.failed', { reason: result.reason ?? result.registration })}</span>
            </div>
            {result.log.length ? <details><summary>{t('check.showLog')}</summary><pre className="log" tabIndex={0}>{result.log.join('\n')}</pre></details> : null}
          </div>
        ) : null}

        <Group icon={<IconKey />} title={t('editor.group.signin')}>{form.primary.map(field)}</Group>
        <Group icon={<IconShield />} title={t('editor.group.security')}>{security.map(field)}</Group>
        <details className="more">
          <summary><IconChevronDown /><IconSliders />{t('editor.group.connection')}<span className="field__hint">{t('editor.group.connection.hint')}</span></summary>
          <div className="fields">{connection.map(field)}</div>
        </details>
      </div>

      <footer className="editor__foot form-foot">
        <p className="hint">{footHint || ' '}</p>
        {!isNew ? <Button kind="danger" onClick={() => { saved.current = true; store.remove(id); onDone(); }}>{t('action.delete')}</Button> : null}
        <Button busy={checking} disabled={!online || missing.length > 0}
          onClick={() => store.check({ ...account, id }, password)}>{checking ? t('action.checking') : t('action.check')}</Button>
        <Button kind="primary" disabled={missing.length > 0 || Object.keys(errors).length > 0 || !dirty} onClick={save}>{t('action.save')}</Button>
      </footer>
    </article>
  );

  // Plain functions, not components: a component declared here would remount the fields on
  // every keystroke and drop the focus.
  function field(info: FieldInfo, index: number) {
    const label = t(`field.${info.key}` as Key);
    const hint = t(`field.${info.key}.hint` as Key);
    const inputId = `f-${info.key}`;
    const labelled = !['transport', 'srtp', 'codecs'].includes(info.key);
    return (
      <div key={info.key} className={`fld${WIDE.has(info.key) ? ' fld--wide' : ''}`}>
        {labelled ? <label htmlFor={inputId}>{label}</label> : <span className="fld__label">{label}</span>}
        {control(info, inputId, label, index === 0 && info.key === 'displayName')}
        {errors[info.key] ? <span className="field__error">{errors[info.key]}</span> : <span className="field__hint">{hint}</span>}
      </div>
    );
  }

  function control(info: FieldInfo, inputId: string, label: string, isFirst: boolean) {
    const value = account[info.key] ?? info.defaultValue;
    if (info.key === 'transport') {
      return <Seg label={label} value={String(value ?? 'udp')} onChange={(v) => apply(transportPatch(account, v))}
        options={(info.enumValues ?? []).map((v) => ({ value: v, label: v.toUpperCase() }))} />;
    }
    if (info.key === 'srtp') {
      return <Choice id={inputId} label={label} value={String(value ?? 'disabled')} onChange={(v) => apply({ srtp: v })}
        options={(info.enumValues ?? []).map((v) => ({ value: v, label: t(`srtp.${v}` as Key), note: t(`srtp.${v}.note` as Key) }))} />;
    }
    if (info.type === 'enum') {
      return <Choice id={inputId} label={label} value={String(value ?? '')} onChange={(v) => apply({ [info.key]: v })}
        options={(info.enumValues ?? []).map((v) => ({ value: v, label: v }))} />;
    }
    if (info.type === 'codecs') {
      const values = Array.isArray(value) ? (value as string[]) : [];
      return <ToggleChips label={label} values={values} onChange={(v) => { if (v.length) apply({ codecs: v }); }}
        options={(info.enumValues ?? []).map((v) => ({ value: v, label: CODEC_LABELS[v] ?? v }))} />;
    }
    if (info.type === 'boolean') {
      return <Switch on={Boolean(value)} label={label} onChange={(on) => apply({ [info.key]: on })} />;
    }
    return (
      <input id={inputId} ref={isFirst ? first : undefined} className={`input${info.type === 'integer' ? ' num' : ''}`}
        type={info.sensitive ? 'password' : 'text'} inputMode={info.type === 'integer' ? 'numeric' : undefined}
        value={raw[info.key] ?? ''} autoComplete="off" spellCheck={false}
        autoCapitalize={info.key === 'domain' ? 'none' : undefined}
        placeholder={info.sensitive && hadPassword ? t('editor.passwordKeep') : info.type === 'integer' && info.defaultValue !== undefined ? String(info.defaultValue) : undefined}
        onChange={(e) => onText(info, e.target.value)} />
    );
  }
}

function Group({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="group">
      <div className="group__head">{icon}<h3>{title}</h3></div>
      <div className="fields">{children}</div>
    </section>
  );
}

function rawOf(fields: FieldInfo[], account: Record<string, unknown>): Record<string, string> {
  const raw: Record<string, string> = {};
  for (const f of fields) {
    if ((f.type === 'string' || f.type === 'integer') && !f.sensitive && account[f.key] !== undefined && account[f.key] !== null) raw[f.key] = String(account[f.key]);
  }
  return raw;
}
